import type { ContactEventStatus, ContactEventType } from '@/api/entity_types/ContactEvent';
import { useTeamStore } from '@/stores/team';
import { useRouter } from 'vue-router';
import RailsUtility from '@/utilities/rails';
import { HumanizString } from '@/utilities/humaniz_string';

export type MeetingDashboardArgs = {
  event_type: ContactEventType;
  id: number;
  start_time: string;
  end_time: string;
  status: ContactEventStatus;
  location?: string | null;
};

export type MeetingLocationType = 'humaniz_conferencing' | 'phone' | 'physical';

export type MeetingLocationInfo = {
  label: string;
  icon: string;
  href?: string;
};

export const useMeetingUsher = () => {
  const router = useRouter();
  const teamStore = useTeamStore();

  const isPastEvent = (meeting: MeetingDashboardArgs): boolean => new Date(meeting.end_time) < new Date();

  const navigateToMeeting = (meeting: MeetingDashboardArgs) => {
    let url = '';
    if (meeting.event_type === 'pipeline_review') {
      url = `/contact_events/${meeting.id}/pipeline_review`;
    } else {
      url = `/contact_events/${meeting.id}/scorecard`;
    }

    router.push(url);
  };

  const actionTitle = (meeting: MeetingDashboardArgs) => {
    if (isLive(meeting)) {
      switch (getLocationType(meeting)) {
        case 'humaniz_conferencing':
          return 'Join Now';
      }
    }
    return meeting.event_type === 'pipeline_review' ? 'Pipeline Review' : 'Script & Scorecard';
  };

  const eventTypeLabel = (meeting: MeetingDashboardArgs) =>
    HumanizString.capitalize(RailsUtility.convertFromEnumFormat(meeting.event_type));

  // Best-effort sniff of the raw location string — we'll address this once we have a better data source.
  const getLocationType = (meeting: MeetingDashboardArgs): MeetingLocationType | null => {
    const location = meeting.location;
    if (!location) return null;
    const loc = location.toLowerCase();
    const trimmed = location.trim();

    if (loc.includes('humaniz') || loc.includes('daily.co')) return 'humaniz_conferencing';
    if (loc.startsWith('tel:') || /^[+\d][\d\s().-]+$/.test(trimmed)) return 'phone';
    return 'physical';
  };

  const getLocationInfo = (meeting: MeetingDashboardArgs): MeetingLocationInfo | null => {
    const type = getLocationType(meeting);
    if (!type) return null;
    const location = meeting.location as string;

    switch (type) {
      case 'humaniz_conferencing':
        return { label: 'Humaniz Conferencing', icon: 'video' };
      case 'phone': {
        const number = location.trim().replace(/^tel:/i, '');
        return { label: number, icon: 'phone', href: `tel:${number}` };
      }
      case 'physical':
        return { label: location, icon: 'map-pin' };
    }
  };

  const isLive = (meeting: MeetingDashboardArgs) => {
    const now = new Date();
    const tenMinutesBefore = new Date(new Date(meeting.start_time).getTime() - 10 * 60 * 1000);
    const nearTime: boolean = now > tenMinutesBefore && now < new Date(meeting.end_time);
    const externalCalendar: boolean = teamStore.team.calendar_option !== 'Humaniz';
    const isHidden: boolean = isPastEvent(meeting) || externalCalendar || meeting.status === 'canceled' || !nearTime;
    return !isHidden;
  };

  return { navigateToMeeting, actionTitle, eventTypeLabel, isPastEvent, isLive, getLocationInfo };
};
